import {survey_config_base} from 'screens/InsomniaSurvey/utils/__survey_config_base';
import {interstitial} from 'screens/InsomniaSurvey/utils/interstitial';
import {dropdownPage, inputPage, radioPage} from 'screens/InsomniaSurvey/utils/page';
import {dateMaskMMDDYYYY, phoneMaskUS} from 'screens/InsomniaSurvey/utils/inputMasks';
import {RiseSurveyConfig} from 'types';
import {calculatedValues} from './calculatedValues';
import {US_STATES} from './config';
import {onboardingPages} from './onboardingPages';
import {validateSleepioEligibility} from './utils';
import InsuranceValidationInterstitial from 'screens/InsomniaSurvey/interstitials/InsuranceValidationInterstitial/InsuranceValidationInterstitial';
import SleepioInfoInterstitial from 'screens/InsomniaSurvey/interstitials/SleepioRxInterstitials/SleepioInfoInterstitial';
import SleepioIntakeInterstitial from 'screens/InsomniaSurvey/interstitials/SleepioRxInterstitials/SleepioIntakeInterstitial';
import SleepioDiscoveryInterstitial from 'screens/InsomniaSurvey/interstitials/SleepioRxInterstitials/SleepioDiscoveryInterstitial';
import SleepioFitWithScienceInterstitial from 'screens/InsomniaSurvey/interstitials/SleepioRxInterstitials/SleepioFitWithScienceInterstitial';
import SleepioNotAvailableInterstitial from 'screens/InsomniaSurvey/interstitials/SleepioRxInterstitials/SleepioNotAvailableInterstitial';
import {handleSleepioContactInfoSubmission} from 'sleepioRx/account';
import hero_image_1 from 'surveys/insomnia_funnel/paywall/assets/hero_image_1.png';
import recommended_by from 'surveys/insomnia_funnel/survey/assets/recommended_by.png';

const SURVEY_TITLE = 'insomnia_funnel';
export const survey_config: RiseSurveyConfig = {
  // Add the images to preload.  These will be preloaded when the survey is loaded.
  // this is optional.
  preloadImages: [hero_image_1, recommended_by],
  ...survey_config_base,
  title: SURVEY_TITLE,
  calculatedValues,
  enableAnswerNewStyle: true,
  onCompleteRedirectTo: survey => {
    // If insurance is not valid, redirect to regular checkout
    // Otherwise, redirect to insomnia clinic checkout
    const insuranceIsValid = survey.getValue('insurance_is_valid');
    return insuranceIsValid === 'yes' ? '/checkout' : '/checkout';
  },

  pages: [
    ...onboardingPages,

    dropdownPage({
      name: 'insurance_state',
      category: 'Sleep Profile',
      title: 'Confirm your state.',
      description: 'Your benefits may include expert sleep help, with most eligible patients paying $0*.',
      choices: US_STATES,
      autoAdvance: false,
      isRequired: true,
      identifyPropertyName: 'state',
      onNext: validateSleepioEligibility,
    }),

    // Validation interstitial - shows appropriate message based on insurance_is_valid
    // which was already set in the previous page's onNext handler
    interstitial({
      name: 'insurance_validation_interstitial',
      category: 'Sleep Profile',
      component: () => <InsuranceValidationInterstitial />,
    }),

    interstitial({
      visibleIf: "{clinic_booking_paused} == true and {sleepio_is_available} == 'no'",
      name: 'sleepio_not_available',
      category: 'Sleep Profile',
      component: () => <SleepioNotAvailableInterstitial />,
      autoAdvance: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_intro',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="intro" />,
      autoAdvance: true,
    }),

    // Reframes the care-model expectation set by `insomnia_care_preference`
    // (many users pick "Video call visits" but SleepioRx is digital-only): a real
    // licensed clinician reviews their answers async and sends next steps (RET-2554).
    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_clinician_review',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="clinicianReview" />,
      autoAdvance: true,
    }),

    // Fit question + its "What the science says" reveal on one screen (RET-2564):
    // answer reveals the science below, Continue advances.
    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_fit_regular_trouble',
      category: 'SleepioRx',
      component: () => (
        <SleepioFitWithScienceInterstitial
          questionName="sleepio_fit_regular_trouble"
          title="Do you regularly have trouble falling asleep, staying asleep, or waking up too early — even when you have the chance to sleep?"
          description="This helps us recommend the right kind of care for you."
          choices={[
            {value: 'agree', text: 'Agree'},
            {value: 'disagree', text: 'Disagree'},
          ]}
          scienceScreen="scienceRegularTrouble"
        />
      ),
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_fit_tried_other_approaches',
      category: 'SleepioRx',
      component: () => (
        <SleepioFitWithScienceInterstitial
          questionName="sleepio_fit_tried_other_approaches"
          title="Have you tried other things to fix your sleep — pills, sleep hygiene, or melatonin?"
          choices={[
            {value: 'yes', text: 'Yes'},
            {value: 'no', text: 'No'},
          ]}
          scienceScreen="scienceOtherApproaches"
        />
      ),
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_fit_heard_of_cbti',
      category: 'SleepioRx',
      component: () => (
        <SleepioFitWithScienceInterstitial
          questionName="sleepio_fit_heard_of_cbti"
          title="Have you heard of Cognitive Behavioral Therapy for Insomnia (CBT-I)?"
          choices={[
            {value: 'yes', text: 'Yes'},
            {value: 'no', text: 'No'},
          ]}
          scienceScreen="scienceCbti"
        />
      ),
    }),

    radioPage({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_fit_open_to_digital',
      category: 'SleepioRx',
      title: 'Would you be open to a structured digital program — covered by insurance — delivered through an app?',
      choices: [
        {value: 'agree', text: 'Agree'},
        {value: 'disagree', text: 'Disagree'},
      ],
      autoAdvance: true,
      isRequired: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_framing_fit',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="framingFit" />,
      autoAdvance: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_framing_nothing_worked',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="framingNothingWorked" />,
      autoAdvance: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_framing_root_cause',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="framingRootCause" />,
      autoAdvance: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_framing_what_it_is',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="framingWhatItIs" />,
      autoAdvance: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_framing_insurance',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="framingInsurance" />,
      autoAdvance: true,
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_framing_progress',
      category: 'SleepioRx',
      component: () => <SleepioInfoInterstitial screen="framingProgress" />,
      autoAdvance: true,
    }),

    // SleepioRx: collect DOB before contact_info — account creation enforces the 18+
    // gate on submit, and Sohar discovery (after contact_info) needs it. Member ID is
    // deferred to after discovery (RET-2560) so a resolved match can pre-fill + skip it.
    inputPage({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_dob',
      category: 'Sleep Profile',
      title: 'What is your date of birth?',
      description: 'We use this to confirm your eligibility for SleepioRx.',
      inputs: [
        {
          name: 'dob',
          placeholder: 'Date of Birth (mm/dd/yyyy)',
          isRequired: true,
          inputMask: dateMaskMMDDYYYY,
        },
      ],
    }),

    // The onNext handler will create the account with loading state and error handling
    // If there's an error, it will be displayed on this page and user can retry
    // CompleteButton now supports onNext handlers (as of this implementation)
    inputPage({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'contact_info',
      category: 'Sleep Profile',
      title: 'Enter your contact info to get your sleep plan with expert session details',
      description: "We'll verify your insurance details up front so you know what's covered—no surprises.",
      inputs: [
        {
          name: 'email',
          placeholder: 'Email',
          isRequired: true,
        },
        {
          name: 'phone',
          placeholder: 'Phone number',
          inputMask: phoneMaskUS,
          isRequired: true,
        },
      ],
      onNext: handleSleepioContactInfoSubmission,
    }),

    // SleepioRx: now that the account + token exist, run Sohar discovery to pre-fill
    // insurance. On a match it sets member_id + discovered_payer_name and
    // discovery_resolved=true; otherwise it falls through to manual member-ID entry.
    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_discovery',
      category: 'SleepioRx',
      component: () => <SleepioDiscoveryInterstitial />,
    }),

    // Manual member-ID entry — only shown when discovery didn't resolve coverage.
    inputPage({
      visibleIf: "{sleepio_is_available} == 'yes' and {discovery_resolved} != true",
      name: 'sleepio_member_id',
      category: 'Sleep Profile',
      title: 'Add your insurance member ID',
      description: "We couldn't pull this from your plan automatically — please enter it from your insurance card.",
      inputs: [
        {
          name: 'member_id',
          placeholder: 'Insurance Member ID',
          isRequired: true,
        },
      ],
    }),

    interstitial({
      visibleIf: "{sleepio_is_available} == 'yes'",
      name: 'sleepio_intake',
      category: 'SleepioRx',
      component: () => <SleepioIntakeInterstitial />,
      autoAdvance: true,
      // Multi-step wizard: SurveyJS can't navigate its internal steps, so page-level
      // back would jump out to discovery and loop. Hide the header back arrow here.
      hideBackButton: true,
    }),
  ],
};

export default survey_config;
